import { Add, Edit, Remove } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNotify, useRecordContext, useTranslate } from "react-admin";
import { v4 as uuid } from "uuid";

import {
  ActivityGoalEnum,
  ActivityGoalItem,
  ActivityGoalProgressDocument,
  ActivityGoalProgressQuery,
  ActivityGoalProgressQueryVariables,
  ActivityGoalsListDocument,
  ActivityGoalsListQuery,
  ActivityGoalsListQueryVariables,
  AddActivityGoalsDocument,
  AddActivityGoalsMutation,
  AddActivityGoalsMutationVariables,
} from "../generated/graphql";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { client } from "../lib/apolloClient";
import { activityCategoryStyle, borderRadius, color, shadows, space, typography } from "../theme/designTokens";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "../theme/dialogStyles";
import { PatientRecord } from "../types/patient";
import { ActivityCategoryIcon } from "./ActivityCategoryIcon";
import { ACTIVITY_GOAL_KINDS, ActivityGoalKind, ActivityGoalProgressByKind, GoalProgress } from "./activityGoalKinds";
import { Icon } from "./icons";
import { RecommendedActivities } from "./RecommendedActivities";

type ActivityGoalProgressRecord = NonNullable<ActivityGoalProgressQuery["activityGoalProgress"]>[number];

type ActivityGoalValues = Record<ActivityGoalKind, number>;

type ActivityGoalRecord = NonNullable<ActivityGoalsListQuery["activityGoalsList"]>[number];

enum ActivityGoalsLoadStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
}

const GOAL_STEP_MINUTES = 5;
const ACTIVITY_GOALS_WIDTH = 920;
const ACTIVITY_GOALS_MODAL_WIDTH = 560;

const buildActivityGoalValues = (activityGoals: ActivityGoalRecord[] = []): ActivityGoalValues => ({
  [ActivityGoalEnum.Cognitive]:
    activityGoals.find(activityGoal => activityGoal.kind === ActivityGoalEnum.Cognitive)?.weeklyGoal ?? 0,
  [ActivityGoalEnum.Physical]:
    activityGoals.find(activityGoal => activityGoal.kind === ActivityGoalEnum.Physical)?.weeklyGoal ?? 0,
  [ActivityGoalEnum.SocialAndCommunicative]:
    activityGoals.find(activityGoal => activityGoal.kind === ActivityGoalEnum.SocialAndCommunicative)?.weeklyGoal ?? 0,
  [ActivityGoalEnum.MentalWellbeing]:
    activityGoals.find(activityGoal => activityGoal.kind === ActivityGoalEnum.MentalWellbeing)?.weeklyGoal ?? 0,
});

const ActivityGoalRows = ({
  values,
  onGoalChange,
}: {
  values: ActivityGoalValues;
  onGoalChange: (kind: ActivityGoalKind, weeklyGoal: number) => void;
}) => {
  const translate = useTranslate();

  return (
    <Stack data-testid="activity-goals-list">
      {ACTIVITY_GOAL_KINDS.map(kind => (
        <Box
          key={kind}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            minHeight: 64,
            borderBottom: `1px solid ${color.datagridBorder}`,
            columnGap: space.sm,
          }}
          data-testid={`activity-goal-${kind.toLowerCase()}`}
        >
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: typography.lineHeight.xs }}>
              {translate(`activityGoals.kind.${kind}`)}
            </Typography>
            <Typography variant="caption" sx={{ color: color.gray, lineHeight: typography.lineHeight.xs }}>
              {translate("activityGoals.minutesPerWeek")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => onGoalChange(kind, Math.max(0, values[kind] - GOAL_STEP_MINUTES))}
              disabled={values[kind] === 0}
              aria-label={translate("activityGoals.decreaseGoal", { kind: translate(`activityGoals.kind.${kind}`) })}
              data-testid={`activity-goal-decrease-${kind.toLowerCase()}`}
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadius.sm,
                backgroundColor: color.secondaryButtonBackground,
                "&:hover": { backgroundColor: color.secondaryButtonHover },
                "&.Mui-disabled": { backgroundColor: color.secondaryButtonDisabled },
              }}
            >
              <Remove sx={{ fontSize: typography.fontSize.lg }} />
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                minWidth: 76,
                px: 1.5,
                py: 0.75,
                border: `1px solid ${color.secondary40}`,
                borderRadius: borderRadius.xs,
                backgroundColor: color.white,
                textAlign: "center",
                fontWeight: 600,
              }}
              data-testid={`activity-goal-value-${kind.toLowerCase()}`}
            >
              {translate("activityGoals.minutes", { minutes: values[kind] })}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onGoalChange(kind, values[kind] + GOAL_STEP_MINUTES)}
              aria-label={translate("activityGoals.increaseGoal", { kind: translate(`activityGoals.kind.${kind}`) })}
              data-testid={`activity-goal-increase-${kind.toLowerCase()}`}
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadius.sm,
                backgroundColor: color.secondaryButtonBackground,
                "&:hover": { backgroundColor: color.secondaryButtonHover },
              }}
            >
              <Add sx={{ fontSize: typography.fontSize.lg }} />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const buildProgressByKind = (progress: ActivityGoalProgressRecord[] = []): ActivityGoalProgressByKind => {
  const byKind = (kind: ActivityGoalKind): GoalProgress => {
    const record = progress.find(item => item.kind === kind);
    return {
      doneMinutes: record?.doneMinutes ?? 0,
      weeklyGoalMinutes: record?.weeklyGoalMinutes ?? 0,
    };
  };
  return {
    [ActivityGoalEnum.Cognitive]: byKind(ActivityGoalEnum.Cognitive),
    [ActivityGoalEnum.Physical]: byKind(ActivityGoalEnum.Physical),
    [ActivityGoalEnum.SocialAndCommunicative]: byKind(ActivityGoalEnum.SocialAndCommunicative),
    [ActivityGoalEnum.MentalWellbeing]: byKind(ActivityGoalEnum.MentalWellbeing),
  };
};

const ActivityGoalProgressCard = ({ kind, progress }: { kind: ActivityGoalKind; progress: GoalProgress }) => {
  const translate = useTranslate();
  const { doneMinutes, weeklyGoalMinutes } = progress;
  const percent = weeklyGoalMinutes > 0 ? Math.min(100, Math.round((doneMinutes / weeklyGoalMinutes) * 100)) : 0;
  const accent = activityCategoryStyle[kind].color;

  return (
    <Box
      sx={{
        p: 1.75,
        border: `1px solid ${color.datagridBorder}`,
        borderRadius: borderRadius.md,
        backgroundColor: color.background2,
      }}
      data-testid={`activity-goal-progress-${kind.toLowerCase()}`}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ActivityCategoryIcon kind={kind} size={30} />
        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: typography.lineHeight.xs }}>
          {translate(`activityGoals.kind.${kind}`)}
        </Typography>
      </Box>
      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Typography
          variant="body2"
          sx={{ color: color.gray }}
          data-testid={`activity-goal-progress-value-${kind.toLowerCase()}`}
        >
          <Box component="span" sx={{ fontSize: typography.fontSize.md, fontWeight: 700, color: color.typographyBase }}>
            {doneMinutes}
          </Box>{" "}
          {translate("activityGoals.progress.outOf", { target: weeklyGoalMinutes })}
        </Typography>
        <Typography variant="body2" sx={{ color: accent, fontWeight: 600 }}>
          {translate("activityGoals.progress.percent", { percent })}
        </Typography>
      </Box>
      <Box
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={translate("activityGoals.progress.ariaLabel", {
          kind: translate(`activityGoals.kind.${kind}`),
          done: doneMinutes,
          target: weeklyGoalMinutes,
        })}
        sx={{
          mt: 1,
          height: 7,
          borderRadius: borderRadius.sm,
          backgroundColor: color.progressTrack,
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: "100%", width: `${percent}%`, borderRadius: borderRadius.sm, backgroundColor: accent }} />
      </Box>
    </Box>
  );
};

const ActivityGoalProgressGrid = ({ progress }: { progress: ActivityGoalProgressByKind }) => (
  <Box
    data-testid="activity-goals-progress-list"
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
      gap: 1.75,
    }}
  >
    {ACTIVITY_GOAL_KINDS.map(kind => (
      <ActivityGoalProgressCard key={kind} kind={kind} progress={progress[kind]} />
    ))}
  </Box>
);

const PatientActivityGoalsEditDialog = ({
  activityGoals,
  onClose,
  onSaved,
  open,
  userId,
}: {
  activityGoals: ActivityGoalRecord[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  open: boolean;
  userId: string;
}) => {
  const [values, setValues] = useState<ActivityGoalValues>(() => buildActivityGoalValues(activityGoals));
  const [isSaving, setIsSaving] = useState(false);
  const handleError = useErrorHandler();
  const translate = useTranslate();
  const notify = useNotify();

  const handleGoalChange = (kind: ActivityGoalKind, weeklyGoal: number) => {
    setValues(prev => ({ ...prev, [kind]: weeklyGoal }));
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const result = await client.mutate<AddActivityGoalsMutation, AddActivityGoalsMutationVariables>({
        mutation: AddActivityGoalsDocument,
        variables: {
          activityGoals: ACTIVITY_GOAL_KINDS.map(
            (kind): ActivityGoalItem => ({
              id: activityGoals.find(activityGoal => activityGoal.kind === kind)?.id ?? uuid(),
              userId,
              kind,
              weeklyGoal: values[kind],
            })
          ),
        },
      });

      if (!result.data?.addActivityGoals) {
        throw new Error("addActivityGoals returned false");
      }

      notify("activityGoals.success", { type: "success" });
      await onSaved();
      onClose();
    } catch (error) {
      handleError(error, translate("activityGoals.error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          ...dialogPaperSx,
          maxWidth: ACTIVITY_GOALS_MODAL_WIDTH,
        },
      }}
    >
      <DialogTitle
        sx={{
          ...dialogTitleSx,
          fontSize: typography.fontSize.lg,
          fontWeight: 600,
        }}
      >
        {translate("activityGoals.editTitle")}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <ActivityGoalRows values={values} onGoalChange={handleGoalChange} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-start", px: 3, pb: 3, pt: 1, backgroundColor: color.white }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          data-testid="save-button"
          startIcon={<Icon type="save" size="medium" />}
          sx={{ minWidth: 104, backgroundColor: color.primary, color: color.white, fontWeight: 600 }}
        >
          {translate(isSaving ? "activityGoals.saving" : "ra.action.save")}
        </Button>
        <Button
          color="secondary"
          onClick={onClose}
          disabled={isSaving}
          data-testid="cancel-button"
          startIcon={<Icon type="close" size="medium" />}
        >
          {translate("ra.action.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PatientActivityGoalsContent = ({
  activityGoals,
  loadActivityGoals,
  progressByKind,
}: {
  activityGoals: ActivityGoalRecord[];
  loadActivityGoals: () => Promise<void>;
  progressByKind: ActivityGoalProgressByKind;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const record = useRecordContext<PatientRecord>();
  const translate = useTranslate();

  if (!record?.userId) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("activityGoals.noDataAvailable")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        mt: 2.75,
        width: ACTIVITY_GOALS_WIDTH,
        maxWidth: "100%",
        px: 3,
        py: 2.75,
        backgroundColor: color.white,
        border: `1px solid ${color.panelBorder}`,
        borderRadius: borderRadius.md,
        boxShadow: shadows.card,
      }}
    >
      <Stack spacing={2.25}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: typography.fontSize.lg, fontWeight: 700 }}>
              {translate("activityGoals.title")}
            </Typography>
            <Typography variant="caption" sx={{ color: color.gray }}>
              {translate("activityGoals.subtitle")}
            </Typography>
          </Box>
          <Button
            color="secondary"
            startIcon={<Edit sx={{ fontSize: typography.fontSize.md }} />}
            onClick={() => setIsDialogOpen(true)}
            data-testid="activity-goals-edit-button"
            sx={{
              backgroundColor: color.secondaryButtonBackground,
              boxShadow: shadows.secondary,
              color: color.typographyBase,
              fontWeight: 600,
              px: 2,
              py: 0.75,
              "&:hover": { backgroundColor: color.secondaryButtonHover, boxShadow: shadows.secondaryHover },
            }}
          >
            {translate("activityGoals.editButton")}
          </Button>
        </Box>
        <ActivityGoalProgressGrid progress={progressByKind} />
      </Stack>
      {isDialogOpen && (
        <PatientActivityGoalsEditDialog
          activityGoals={activityGoals}
          onClose={() => setIsDialogOpen(false)}
          onSaved={loadActivityGoals}
          open={isDialogOpen}
          userId={record.userId}
        />
      )}
    </Box>
  );
};

export const PatientActivityGoalsTab = () => {
  const [activityGoals, setActivityGoals] = useState<ActivityGoalRecord[]>([]);
  const [progress, setProgress] = useState<ActivityGoalProgressRecord[]>([]);
  const [loadStatus, setLoadStatus] = useState(ActivityGoalsLoadStatus.Loading);
  const record = useRecordContext<PatientRecord>();
  const translate = useTranslate();

  const loadActivityGoals = useCallback(async () => {
    if (!record?.userId) return;

    setLoadStatus(ActivityGoalsLoadStatus.Loading);

    try {
      const [goalsResult, progressResult] = await Promise.all([
        client.query<ActivityGoalsListQuery, ActivityGoalsListQueryVariables>({
          query: ActivityGoalsListDocument,
          variables: { userId: record.userId },
          fetchPolicy: "network-only",
        }),
        client.query<ActivityGoalProgressQuery, ActivityGoalProgressQueryVariables>({
          query: ActivityGoalProgressDocument,
          variables: { userId: record.userId },
          fetchPolicy: "network-only",
        }),
      ]);

      setActivityGoals(goalsResult.data.activityGoalsList ?? []);
      setProgress(progressResult.data.activityGoalProgress ?? []);
      setLoadStatus(ActivityGoalsLoadStatus.Loaded);
    } catch (error) {
      setLoadStatus(ActivityGoalsLoadStatus.Error);
    }
  }, [record?.userId]);

  useEffect(() => {
    void loadActivityGoals();
  }, [loadActivityGoals]);

  if (!record?.userId) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("activityGoals.noDataAvailable")}
      </Typography>
    );
  }

  if (loadStatus === ActivityGoalsLoadStatus.Loading) {
    return <Typography variant="body2">{translate("activityGoals.loading")}</Typography>;
  }

  if (loadStatus === ActivityGoalsLoadStatus.Error) {
    return (
      <Typography variant="body2" color="error">
        {translate("activityGoals.loadError")}
      </Typography>
    );
  }

  const progressByKind = buildProgressByKind(progress);

  return (
    <>
      <PatientActivityGoalsContent
        activityGoals={activityGoals}
        loadActivityGoals={loadActivityGoals}
        progressByKind={progressByKind}
      />
      <RecommendedActivities goals={progressByKind} />
    </>
  );
};
